<template>
  <div class="production-preview w-full flex flex-col items-center gap-2">
    <div
      v-if="state === 'loading'"
      class="w-full min-h-[240px] flex flex-col items-center justify-center text-sm text-ink-mute"
    >
      <span class="production-preview__spinner mb-2" aria-hidden="true"></span>
      Generating production preview…
    </div>

    <template v-else-if="state === 'ready' && previewUrl">
      <a
        :href="previewUrl"
        target="_blank"
        rel="noopener"
        class="block w-full"
        title="Open the generated production file in a new tab"
      >
        <img
          :src="previewUrl"
          :alt="`Production file preview for ${product.product_name || 'product'}`"
          class="w-full max-h-[420px] md:max-h-[520px] object-contain bg-surface rounded-lg border border-line"
        />
      </a>
      <p class="text-xs text-ink-mute text-center">
        Generated from the builder design{{ previewRowLabel }} — the same engine as the downloads.
        <span v-if="ungraded" class="text-amber-600">Ungraded (no sizes file on this product).</span>
      </p>
    </template>

    <div v-else class="w-full min-h-[120px] flex flex-col items-center justify-center text-sm text-ink-mute">
      <p>Production preview could not be generated.</p>
      <p v-if="errorMessage" class="text-xs text-ink-mute mt-1">{{ errorMessage }}</p>
      <slot name="fallback"></slot>
    </div>

    <!-- Size tabs: one per distinct roster size, in roster order. -->
    <div
      v-if="sizes.length > 1"
      class="flex flex-wrap justify-center gap-1.5 mt-1"
      role="tablist"
      aria-label="Preview size"
    >
      <button
        v-for="size in sizes"
        :key="size"
        type="button"
        role="tab"
        :aria-selected="size === activeSize"
        class="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
        :class="
          size === activeSize
            ? 'bg-primary text-white border-primary'
            : 'bg-surface text-ink-soft border-line hover:border-primary hover:text-primary'
        "
        :disabled="state === 'loading'"
        @click="activeSize = size"
      >
        {{ size }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Live production preview on the order's product card: composes the product's
 * production file with the SAME engine the downloads use (first roster row's
 * name/number, graded to that row's size when the sizes file has it) and shows
 * the result. Replaces the legacy `production_url` image, which was frozen at
 * cart-add time by the widget and no longer reflects the generator's output.
 *
 * One compose per product per page load (module-level cache); the raw
 * composite is shown — browsers render the atlas clip masks fine, so the
 * CorelDRAW-oriented flattening the downloads do is not needed here.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { FactoryProduct } from '@/models';
import type { ProductRosterDetail } from '@/models/ProductRosterDetail';
import {
  createProductionAssetCache,
  useProductionFileEngine,
} from '@/composables/useProductionFileEngine';
import { buildComposeInputForRow } from '@/utils/productionFile/composeInput';

const props = defineProps<{
  product: FactoryProduct;
}>();

type State = 'loading' | 'ready' | 'error';

interface CachedPreview {
  svg: string;
  rowLabel: string;
  ungraded: boolean;
}

/** productId|designUrl -> composed preview, shared across re-renders of the page. */
const previewCache = new Map<string, Promise<CachedPreview>>();
const cache = createProductionAssetCache();

const state = ref<State>('loading');
const previewUrl = ref<string | null>(null);
const previewRowLabel = ref('');
const ungraded = ref(false);
const errorMessage = ref('');

/** Distinct roster sizes in roster order — the preview tabs. */
const sizes = computed<string[]>(() => {
  const seen = new Set<string>();
  for (const row of props.product.product_roster_detail ?? []) {
    const size = String(row?.size ?? '').trim();
    if (size) seen.add(size);
  }
  return [...seen];
});
const activeSize = ref<string>('');

const cacheKey = computed(
  () =>
    `${props.product.id ?? ''}|${props.product.production_design_url ?? ''}|` +
    `${props.product.design_sizes_url ?? ''}|${activeSize.value}`
);

/** The roster row the preview shows: first row of the active size, else the first row. */
function previewRow(): ProductRosterDetail {
  const rows = props.product.product_roster_detail ?? [];
  const row =
    rows.find((r) => String(r?.size ?? '').trim() === activeSize.value) ?? rows[0];
  return (row ?? { size: '', text: '', number: '', quantity: 1 }) as ProductRosterDetail;
}

async function composePreview(): Promise<CachedPreview> {
  const engine = useProductionFileEngine(cache);
  const row = previewRow();
  const input = buildComposeInputForRow(props.product, row);
  const hasSize = !!(row.size ?? '').trim();
  if (!hasSize) input.sizesUrl = undefined;

  let result = await engine.compose(input);
  // The first row's size may not exist in the sizes file — show the base size
  // rather than nothing (the downloads report that row properly).
  if (!result.sizeFound && input.sizesUrl) {
    result = await engine.compose({ ...input, sizesUrl: undefined });
  }
  if (!result.sizeFound || !result.svg) throw new Error('The production file could not be composed.');

  const parts = [row.size, row.text, row.number].map((v) => String(v ?? '').trim()).filter(Boolean);
  return {
    svg: result.svg,
    rowLabel: parts.length ? ` (${parts.join(' · ')})` : '',
    ungraded: result.warnings.includes('ungraded-base-size') || !input.sizesUrl,
  };
}

function revoke(): void {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = null;
}

async function load(): Promise<void> {
  state.value = 'loading';
  errorMessage.value = '';
  revoke();
  if (!props.product.production_design_url) {
    state.value = 'error';
    errorMessage.value = 'This product has no production design file.';
    return;
  }
  const key = cacheKey.value;
  let pending = previewCache.get(key);
  if (!pending) {
    pending = composePreview();
    previewCache.set(key, pending);
    pending.catch(() => previewCache.delete(key));
  }
  try {
    const preview = await pending;
    if (key !== cacheKey.value) return; // product changed while composing
    previewUrl.value = URL.createObjectURL(new Blob([preview.svg], { type: 'image/svg+xml' }));
    previewRowLabel.value = preview.rowLabel;
    ungraded.value = preview.ungraded;
    state.value = 'ready';
  } catch (error) {
    console.error('Production preview failed', error);
    errorMessage.value = error instanceof Error ? error.message : String(error);
    state.value = 'error';
  }
}

// Keep the active tab valid when the roster changes (edits, product switch).
watch(
  sizes,
  (list) => {
    if (!list.includes(activeSize.value)) activeSize.value = list[0] ?? '';
  },
  { immediate: true }
);
onMounted(load);
watch(cacheKey, load);
onBeforeUnmount(revoke);
</script>

<style scoped>
.production-preview__spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid var(--border);
  border-top-color: var(--accent);
  animation: production-preview-spin 0.8s linear infinite;
}
@keyframes production-preview-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
